import { createFileRoute } from '@tanstack/react-router';

import { Card } from '@apps/web/components/ui/card';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { useAdminOrganizationDefaultSettings } from '@apps/web/swr_data_fetching/admin_organization_queries';

export const Route = createFileRoute(
    '/_app/admin/organization-default-settings',
)({
    head: () => ({
        meta: [
            {
                title: 'Org Default Settings | Phoebe Admin',
            },
        ],
    }),
    component: OrganizationDefaultSettingsPage,
});

function ValueCell({ value }: { value: unknown }) {
    if (Array.isArray(value) || (value !== null && typeof value === 'object')) {
        return (
            <div className="max-w-full overflow-auto">
                <pre className="border-border bg-muted/50 text-foreground rounded border px-2 py-1 font-mono text-[11px] leading-tight break-words whitespace-pre-wrap">
                    {JSON.stringify(value, null, 2)}
                </pre>
            </div>
        );
    }
    return (
        <code className="border-border bg-muted/50 text-foreground block rounded border px-2 py-1 font-mono text-[11px] leading-tight break-words whitespace-pre-wrap">
            {String(value)}
        </code>
    );
}

function KeyValueTable({
    title,
    data,
}: {
    title: string;
    data: Record<string, unknown>;
}) {
    const rows = Object.entries(data);
    return (
        <Card className="p-0">
            <div className="border-b px-3 py-2">
                <h3 className="text-foreground text-sm font-medium">{title}</h3>
            </div>
            <ScrollArea>
                <Table className="table-fixed">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-muted-foreground w-56 text-[11px] tracking-wide uppercase">
                                Setting
                            </TableHead>
                            <TableHead className="text-muted-foreground text-[11px] tracking-wide uppercase">
                                Value
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map(([key, value]) => (
                            <TableRow
                                key={key}
                                className="odd:bg-gray-50/60 dark:odd:bg-white/5"
                            >
                                <TableCell className="text-foreground py-1.5 pr-2 pl-3 align-top text-xs font-medium">
                                    {key}
                                </TableCell>
                                <TableCell className="py-1.5 pr-3 pl-2 align-top">
                                    <ValueCell value={value} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </ScrollArea>
        </Card>
    );
}

function OrganizationDefaultSettingsPage() {
    const { data, isLoading, error } = useAdminOrganizationDefaultSettings();

    if (isLoading) {
        return (
            <div className="flex flex-1 items-center justify-center p-8">
                <div className="text-muted-foreground text-sm">
                    Loading default settings…
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex flex-1 items-center justify-center p-8">
                <div className="text-sm text-red-600 dark:text-red-400">
                    Failed to load default settings
                </div>
            </div>
        );
    }

    return (
        <div className="mb-4 flex flex-1 flex-col gap-4 p-6">
            <div>
                <h1 className="text-lg leading-tight font-semibold">
                    Organization Default Settings
                </h1>
                <p className="text-muted-foreground mt-0.5 text-xs">
                    These will apply to NEW organizations when we create them.
                    Right now, to adjust we need to do a database migration.
                </p>
            </div>
            <div className="space-y-4">
                <KeyValueTable
                    title="Shift Confirmation Settings"
                    data={
                        data.shift_confirmation_settings as Record<
                            string,
                            unknown
                        >
                    }
                />
                <KeyValueTable
                    title="Open Shift Settings"
                    data={data.callout_settings as Record<string, unknown>}
                />
                <KeyValueTable
                    title="General Settings"
                    data={data.general_settings as Record<string, unknown>}
                />
            </div>
        </div>
    );
}
